import React, { useState, useMemo, useCallback } from 'react';
import { LineChart as LineChartIcon } from 'lucide-react'; // aliased — recharts also exports LineChart
import {
  ResponsiveContainer, ComposedChart, Line, BarChart, Bar, Cell,
  XAxis, YAxis, Tooltip as RechartsTip, CartesianGrid,
} from 'recharts';
import { useEngine } from '../context/MarketContext';
import { useAuth } from '../context/AuthContext';
import { useVwapData } from '../store/hooks';
import { fetchTradeSeries } from '../api/series';
import type { PgInterval } from '../api/series';
import { INSTRUMENTS, resolveBySymbol } from '../constants/instruments';
import { Panel } from '../components/ui';

// ── Module-level helpers ──────────────────────────────────────────────────────

function datePreset(preset: '1D' | '1W' | '1M' | '3M'): { start: string; end: string } {
  const pad = (n: number) => String(n).padStart(2, '0');
  const fmt = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const now = new Date();
  const back = new Date(now);
  if (preset === '1D') back.setDate(back.getDate() - 1);
  if (preset === '1W') back.setDate(back.getDate() - 7);
  if (preset === '1M') back.setMonth(back.getMonth() - 1);
  if (preset === '3M') back.setMonth(back.getMonth() - 3);
  return { start: fmt(back), end: fmt(now) };
}

function annualizationFactor(b: PgInterval): number {
  const map: Partial<Record<PgInterval, number>> = {
    '1 second': 23400, '5 seconds': 4680, '15 seconds': 1560,
    '30 seconds': 780, '1 minute': 390, '5 minutes': 78,
    '15 minutes': 26, '30 minutes': 13, '1 hour': 6.5,
  };
  return (map[b] ?? 390) * 252;
}

function calcVolatility(prices: number[], b: PgInterval): number | null {
  if (prices.length < 3) return null;
  const returns: number[] = [];
  for (let i = 1; i < prices.length; i++) {
    if (prices[i - 1] > 0 && prices[i] > 0)
      returns.push(Math.log(prices[i] / prices[i - 1]));
  }
  if (returns.length < 2) return null;
  const mean = returns.reduce((s, r) => s + r, 0) / returns.length;
  const variance = returns.reduce((s, r) => s + (r - mean) ** 2, 0) / (returns.length - 1);
  return Math.sqrt(variance * annualizationFactor(b)) * 100;
}

function isIntradayBucket(b: PgInterval): boolean {
  return ['1 second', '5 seconds', '15 seconds', '30 seconds', '1 minute', '5 minutes'].includes(b);
}

function makeTicker(b: PgInterval): (ts: string) => string {
  return (ts: string) => {
    const d = new Date(ts);
    if (isNaN(d.getTime())) return ts;
    if (isIntradayBucket(b)) {
      return d.toLocaleTimeString('en-TZ', {
        timeZone: 'Africa/Dar_es_Salaam',
        hour: '2-digit', minute: '2-digit', hour12: false,
      });
    }
    return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
  };
}

function exportCSV(data: SeriesRow[], symbol: string): void {
  const csv = [
    'Timestamp,Price,VWAP,Volume',
    ...data.map(r => `${r.ts},${r.price},${r.vwap ?? ''},${r.volume}`),
  ].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `market-stats-${symbol}-${Date.now()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function fmtTZS(n: number): string {
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return n.toFixed(2);
}

function fmtDatetime(ts: string): string {
  try {
    return new Date(ts).toLocaleString('en-TZ', { timeZone: 'Africa/Dar_es_Salaam' });
  } catch { return ts; }
}

// ── Types ─────────────────────────────────────────────────────────────────────

type SeriesRow = { ts: string; price: number; volume: number; vwap: number | null };
type SortCol = 'ts' | 'price' | 'vwap' | 'volume';
type ChartRow = SeriesRow & { volColor: string };

const BUCKETS: { label: string; value: PgInterval }[] = [
  { label: '1m',  value: '1 minute' },
  { label: '5m',  value: '5 minutes' },
  { label: '15m', value: '15 minutes' },
  { label: '30m', value: '30 minutes' },
  { label: '1h',  value: '1 hour' },
];

// ── Regime colors (from Watchlist.tsx) ────────────────────────────────────────

const REGIME_COLORS: Record<string, string> = {
  ACTIVE: '#36c98e', accumulation: '#36c98e',
  QUIET: '#4da3ff', neutral: '#4da3ff',
  DORMANT: '#7a8693',
  VOLATILE: '#ff5c5c', distribution: '#ff5c5c',
};

// ── Component ─────────────────────────────────────────────────────────────────

export default function MarketStatsPage() {
  const { regimeBySymbol, inventoryBySymbol } = useEngine();
  const { token } = useAuth();

  // Controls
  const [sym, setSym] = useState<string>('LPPJ');
  const [freeText, setFreeText] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [bucket, setBucket] = useState<PgInterval>('1 minute');

  // Fetch state
  const [rows, setRows] = useState<SeriesRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // Table sort
  const [sortCol, setSortCol] = useState<SortCol>('ts');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const effectiveSym = freeText.trim().toUpperCase() || sym;

  // Live context values for Zone 4 (always current regardless of date selection)
  const regime = resolveBySymbol(regimeBySymbol, effectiveSym);
  const vwapArr = useVwapData(effectiveSym);
  const currentVwap = vwapArr.length > 0 ? vwapArr[vwapArr.length - 1]?.vwap : null;
  const inventory = resolveBySymbol(inventoryBySymbol, effectiveSym);

  // ── Fetch handler ────────────────────────────────────────────────────────────
  const handleFetch = useCallback(async () => {
    if (!startDate || !endDate) {
      setFetchError('Select a date range first.');
      return;
    }
    setLoading(true);
    setFetchError(null);
    setRows([]);
    try {
      const raw = await fetchTradeSeries(
        effectiveSym,
        `${startDate}T00:00:00.000Z`,
        `${endDate}T23:59:59.999Z`,
        bucket,
        token,
      );
      const clean: SeriesRow[] = raw
        .filter(r => r.price != null)
        .map(r => ({
          ts: r.ts,
          price: r.price as number,
          volume: r.volume,
          vwap: r.vwap ?? null,
        }));
      setRows(clean);
      if (clean.length === 0) setFetchError('No data returned for this symbol and date range.');
    } catch (e) {
      setFetchError(e instanceof Error ? e.message : 'Fetch failed');
    } finally {
      setLoading(false);
    }
  }, [effectiveSym, startDate, endDate, bucket]);

  // ── Derived computations ─────────────────────────────────────────────────────
  const stats = useMemo(() => {
    if (rows.length === 0) return null;
    const prices = rows.map(r => r.price);
    const first = prices[0];
    const last = prices[prices.length - 1];
    const periodReturn = first > 0 ? ((last - first) / first) * 100 : null;
    const vol = calcVolatility(prices, bucket);
    const totalVolume = rows.reduce((s, r) => s + r.volume, 0);
    const vwapVals = rows.filter(r => r.vwap != null).map(r => r.vwap as number);
    const avgVwap = vwapVals.length > 0
      ? vwapVals.reduce((s, v) => s + v, 0) / vwapVals.length
      : null;
    const high = Math.max(...prices);
    const low = Math.min(...prices);
    return { periodReturn, vol, totalVolume, avgVwap, high, low, ticks: rows.length };
  }, [rows, bucket]);

  const chartRows = useMemo((): ChartRow[] =>
    rows.map((r, i) => ({
      ...r,
      volColor: i === 0 || r.price >= rows[i - 1].price ? '#36c98e' : '#ff5c5c',
    })),
    [rows]
  );

  const sortedRows = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      let av: number, bv: number;
      if (sortCol === 'ts')       { av = new Date(a.ts).getTime(); bv = new Date(b.ts).getTime(); }
      else if (sortCol === 'vwap')  { av = a.vwap ?? 0;              bv = b.vwap ?? 0; }
      else if (sortCol === 'price') { av = a.price;                   bv = b.price; }
      else                          { av = a.volume;                  bv = b.volume; }
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return copy;
  }, [rows, sortCol, sortDir]);

  function handleSort(col: SortCol) {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  }

  // ── Styles ───────────────────────────────────────────────────────────────────
  const card: React.CSSProperties = {
    backgroundColor: '#10151b',
    borderRadius: 8,
    border: '1px solid #2a3540',
    padding: '12px 16px',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 9, color: '#46525e', fontWeight: 700, letterSpacing: '0.06em', marginBottom: 4,
  };

  const thStyle: React.CSSProperties = {
    padding: '6px 10px', fontSize: 10, fontWeight: 700, color: '#46525e',
    letterSpacing: '0.06em', cursor: 'pointer', userSelect: 'none',
    textAlign: 'right',
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <LineChartIcon size={16} color="#7e8a96" />
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#d7dde4' }}>Market Stats</h2>
      </div>

      {/* ── Zone 0: Controls ── */}
      <div style={{ ...card, padding: '14px 16px' }}>
        {/* Row 1: Symbol chips */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
          <span style={labelStyle}>SYMBOL</span>
          {INSTRUMENTS.map(inst => {
            const isActive = !freeText.trim() && sym === inst.secId;
            return (
              <button
                key={inst.secId}
                onClick={() => { setSym(inst.secId); setFreeText(''); }}
                style={{
                  padding: '4px 10px', borderRadius: 4, cursor: 'pointer',
                  fontSize: 11, fontWeight: 700,
                  background: isActive ? 'rgba(77,163,255,0.15)' : '#0a0d10',
                  color: isActive ? '#93c5fd' : '#7a8693',
                  border: `1px solid ${isActive ? '#4da3ff' : '#2a3540'}`,
                }}
              >
                {inst.label}
              </button>
            );
          })}
          <input
            type="text"
            value={freeText}
            onChange={e => setFreeText(e.target.value)}
            placeholder="Unlisted secId…"
            style={{
              background: '#0a0d10', border: `1px solid ${freeText.trim() ? '#4da3ff' : '#2a3540'}`,
              color: '#d7dde4', borderRadius: 4, padding: '4px 10px',
              fontSize: 11, width: 120,
            }}
          />
        </div>

        {/* Row 2: Date range + bucket + fetch */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={labelStyle}>RANGE</span>
          <input
            type="date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            style={{
              background: '#0a0d10', border: '1px solid #2a3540', color: '#d7dde4',
              borderRadius: 4, padding: '4px 8px', fontSize: 11,
            }}
          />
          <span style={{ color: '#46525e', fontSize: 11 }}>→</span>
          <input
            type="date"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            style={{
              background: '#0a0d10', border: '1px solid #2a3540', color: '#d7dde4',
              borderRadius: 4, padding: '4px 8px', fontSize: 11,
            }}
          />

          {/* Preset buttons */}
          {(['1D', '1W', '1M', '3M'] as const).map(p => (
            <button
              key={p}
              onClick={() => { const { start, end } = datePreset(p); setStartDate(start); setEndDate(end); }}
              style={{
                padding: '3px 9px', background: '#0a0d10', border: '1px solid #2a3540',
                borderRadius: 4, color: '#7a8693', fontSize: 10, cursor: 'pointer', fontWeight: 700,
              }}
              onMouseEnter={e => (e.currentTarget.style.borderColor = '#46525e')}
              onMouseLeave={e => (e.currentTarget.style.borderColor = '#2a3540')}
            >
              {p}
            </button>
          ))}

          {/* Resolution selector */}
          <span style={labelStyle}>BUCKET</span>
          <select
            value={bucket}
            onChange={e => setBucket(e.target.value as PgInterval)}
            style={{
              background: '#0a0d10', border: '1px solid #2a3540', color: '#d7dde4',
              borderRadius: 4, padding: '4px 8px', fontSize: 11, cursor: 'pointer',
            }}
          >
            {BUCKETS.map(b => (
              <option key={b.value} value={b.value}>{b.label}</option>
            ))}
          </select>

          {/* Fetch */}
          <button
            onClick={handleFetch}
            disabled={loading}
            style={{
              padding: '5px 16px', background: loading ? '#10151b' : '#4da3ff',
              border: 'none', borderRadius: 4, color: loading ? '#7a8693' : '#fff',
              fontSize: 11, fontWeight: 700, cursor: loading ? 'default' : 'pointer',
            }}
          >
            {loading ? 'Loading…' : 'Fetch'}
          </button>

          {fetchError && (
            <span style={{ fontSize: 10, color: '#ff5c5c' }}>{fetchError}</span>
          )}
        </div>
      </div>

      {/* ── Zone 1: Stats strip (shown after data is fetched) ── */}
      {stats && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {[
            {
              label: 'PERIOD RETURN',
              value: stats.periodReturn != null ? `${stats.periodReturn >= 0 ? '+' : ''}${stats.periodReturn.toFixed(2)}%` : '—',
              color: stats.periodReturn == null ? '#7e8a96' : stats.periodReturn >= 0 ? '#36c98e' : '#ff5c5c',
            },
            {
              label: 'ANN. VOLATILITY',
              value: stats.vol != null ? `${stats.vol.toFixed(1)}%` : '—',
              color: '#7e8a96',
            },
            {
              label: 'TOTAL VOLUME',
              value: stats.totalVolume > 0 ? stats.totalVolume.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '—',
              color: '#d7dde4',
            },
            {
              label: 'AVG VWAP',
              value: stats.avgVwap != null ? `TZS ${stats.avgVwap.toFixed(2)}` : '—',
              color: '#ff8c3a',
            },
            {
              label: 'PERIOD HIGH',
              value: `TZS ${stats.high.toFixed(2)}`,
              color: '#36c98e',
            },
            {
              label: 'PERIOD LOW',
              value: `TZS ${stats.low.toFixed(2)}`,
              color: '#ff5c5c',
            },
          ].map(({ label, value, color }) => (
            <div key={label} style={{
              flex: 1, minWidth: 110, background: '#10151b', borderRadius: 8,
              border: '1px solid #2a3540', padding: '10px 14px',
            }}>
              <div style={labelStyle}>{label}</div>
              <div style={{ fontSize: 15, fontWeight: 700, color, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
            </div>
          ))}
        </div>
      )}

      {/* ── Zone 2 & 3: Charts (shown after data is fetched) ── */}
      {chartRows.length > 0 && (
        <Panel title="Price & VWAP" badge={effectiveSym}>
          {/* Price + VWAP chart */}
          <ResponsiveContainer width="100%" height={180}>
            <ComposedChart data={chartRows} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#10151b" />
              <XAxis
                dataKey="ts"
                tick={{ fontSize: 9, fill: '#46525e' }}
                tickLine={false}
                axisLine={false}
                tickFormatter={makeTicker(bucket) as (value: any, index: number) => string}
                interval="preserveStartEnd"
              />
              <YAxis
                tick={{ fontSize: 9, fill: '#46525e' }}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v: number) => fmtTZS(v)}
                width={52}
                domain={['auto', 'auto']}
              />
              <RechartsTip
                contentStyle={{ background: '#0a0d10', border: '1px solid #2a3540', borderRadius: 6, fontSize: 11 }}
                labelFormatter={(ts: any) => fmtDatetime(String(ts))}
                formatter={(value: any, name: any) => {
                  const n = Number(value);
                  if (name === 'price') return [`${n.toFixed(2)} TZS`, 'Price'];
                  if (name === 'vwap')  return [`${n.toFixed(2)} TZS`, 'VWAP'];
                  return [String(value), String(name)];
                }}
              />
              <Line type="monotone" dataKey="price" stroke="#4da3ff" strokeWidth={1.5} dot={false} connectNulls />
              <Line type="monotone" dataKey="vwap"  stroke="#ff8c3a" strokeWidth={1.5} dot={false} connectNulls />
            </ComposedChart>
          </ResponsiveContainer>

          {/* Legend */}
          <div style={{ display: 'flex', gap: 14, marginTop: 6, marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <div style={{ width: 20, height: 2, backgroundColor: '#4da3ff' }} />
              <span style={{ fontSize: 9, color: '#46525e' }}>Price</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <div style={{ width: 20, height: 2, backgroundColor: '#ff8c3a' }} />
              <span style={{ fontSize: 9, color: '#46525e' }}>VWAP</span>
            </div>
          </div>

          {/* Volume bars */}
          <div style={{ fontSize: 10, color: '#46525e', fontWeight: 700, letterSpacing: '0.06em', marginBottom: 6 }}>VOLUME</div>
          <ResponsiveContainer width="100%" height={70}>
            <BarChart data={chartRows} margin={{ top: 0, right: 4, bottom: 0, left: 0 }}>
              <XAxis dataKey="ts" tick={false} axisLine={false} tickLine={false} height={0} />
              <YAxis hide />
              <Bar dataKey="volume" isAnimationActive={false}>
                {chartRows.map((entry, index) => (
                  <Cell key={`vol-${index}`} fill={entry.volColor} fillOpacity={0.7} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      )}

      {/* ── Zone 4: Live snapshot (always rendered) ── */}
      <Panel title="Current market state" badge={`${effectiveSym} · LIVE`} badgeTone="ok">
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {/* Regime */}
          <div>
            <div style={labelStyle}>REGIME</div>
            {regime ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{
                  width: 8, height: 8, borderRadius: '50%',
                  backgroundColor: REGIME_COLORS[regime.regime] ?? '#7a8693',
                }} />
                <span style={{ fontSize: 14, fontWeight: 700, color: '#d7dde4' }}>
                  {regime.regime}
                </span>
                <span style={{ fontSize: 10, color: '#7a8693' }}>
                  {(regime.confidence * 100).toFixed(0)}% conf
                </span>
              </div>
            ) : (
              <span style={{ fontSize: 13, color: '#2a3540' }}>No data</span>
            )}
          </div>

          {/* Current VWAP */}
          <div>
            <div style={labelStyle}>CURRENT VWAP</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#ff8c3a', fontVariantNumeric: 'tabular-nums' }}>
              {currentVwap != null ? `TZS ${currentVwap.toFixed(2)}` : '—'}
            </div>
          </div>

          {/* Inventory */}
          {inventory && (
            <>
              <div>
                <div style={labelStyle}>POSITION</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#d7dde4', fontVariantNumeric: 'tabular-nums' }}>
                  {inventory.quantity.toLocaleString()}
                </div>
              </div>
              <div>
                <div style={labelStyle}>SKEW</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: inventory.skew > 0.1 ? '#36c98e' : inventory.skew < -0.1 ? '#ff5c5c' : '#7e8a96' }}>
                  {(inventory.skew * 100).toFixed(0)}%
                </div>
              </div>
              <div>
                <div style={labelStyle}>TRADEABLE</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: inventory.can_buy ? '#36c98e' : '#2a3540' }}>
                    {inventory.can_buy ? '● BUY' : '✕ BUY'}
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: inventory.can_sell ? '#36c98e' : '#2a3540' }}>
                    {inventory.can_sell ? '● SELL' : '✕ SELL'}
                  </span>
                </div>
              </div>
            </>
          )}

          {!regime && !inventory && (
            <span style={{ fontSize: 11, color: '#2a3540', fontStyle: 'italic' }}>
              No live data for {effectiveSym} — symbol may not be active in the current session
            </span>
          )}
        </div>

        {/* Deferred note */}
        <div style={{ marginTop: 10, fontSize: 9, color: '#2a3540', fontStyle: 'italic' }}>
          {/* TODO: Historical regime overlay requires GET /api/regime/{symbol}/history endpoint */}
          {/* TODO: OFI/imbalance history requires widening /api/series/{symbol}/combined to support date ranges */}
          {/* TODO: Corporate actions require a new corporate_actions table and endpoint */}
          Historical regime phases, OFI signals, and corporate actions require additional backend endpoints.
        </div>
      </Panel>

      {/* ── Zone 5: Data table + Export ── */}
      {rows.length > 0 && (
        <Panel title="Data table" badge={`${rows.length} buckets`}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <span style={{ fontSize: 9, color: '#2a3540', fontWeight: 400 }}>
              {startDate} → {endDate}
            </span>
            <button
              onClick={() => exportCSV(rows, effectiveSym)}
              style={{
                padding: '4px 12px', background: '#0a0d10', border: '1px solid #2a3540',
                borderRadius: 4, color: '#7a8693', fontSize: 10, fontWeight: 700, cursor: 'pointer',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#46525e'; e.currentTarget.style.color = '#7e8a96'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a3540'; e.currentTarget.style.color = '#7a8693'; }}
            >
              Export CSV
            </button>
          </div>

          {/* Table header */}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #2a3540' }}>
                {([
                  { col: 'ts',     label: 'TIMESTAMP',   align: 'left' },
                  { col: 'price',  label: 'PRICE (TZS)', align: 'right' },
                  { col: 'vwap',   label: 'VWAP (TZS)',  align: 'right' },
                  { col: 'volume', label: 'VOLUME',       align: 'right' },
                ] as { col: SortCol; label: string; align: 'left' | 'right' }[]).map(({ col, label, align }) => (
                  <th
                    key={col}
                    onClick={() => handleSort(col)}
                    style={{ ...thStyle, textAlign: align }}
                  >
                    {label}
                    {sortCol === col && (
                      <span style={{ marginLeft: 3 }}>{sortDir === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
          </table>
          <div style={{ maxHeight: 280, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <tbody>
                {sortedRows.map((r, i) => (
                  <tr
                    key={i}
                    style={{ borderBottom: '1px solid #0a0d10' }}
                    onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#0a0d10')}
                    onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                  >
                    <td style={{ padding: '6px 10px', color: '#7a8693', fontFamily: 'monospace', fontSize: 10 }}>
                      {fmtDatetime(r.ts)}
                    </td>
                    <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600, color: '#d7dde4', fontVariantNumeric: 'tabular-nums' }}>
                      {r.price.toFixed(2)}
                    </td>
                    <td style={{ padding: '6px 10px', textAlign: 'right', color: r.vwap != null ? '#ff8c3a' : '#2a3540', fontVariantNumeric: 'tabular-nums' }}>
                      {r.vwap != null ? r.vwap.toFixed(2) : '—'}
                    </td>
                    <td style={{ padding: '6px 10px', textAlign: 'right', color: '#7e8a96', fontVariantNumeric: 'tabular-nums' }}>
                      {r.volume.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}
    </div>
  );
}
